import { useId, useState } from 'react';
import { CalendarIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { DatePicker } from '@/components/ui/DatePicker';
import { parseDateKey, type DateRange } from '@/components/ui/date-picker-utils';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import type { ReferenceRef } from '@/features/portal/types';
import { useCreateLeave } from '../useTimesheets';
import type { Timesheet } from '../types';

export const TimesheetLeaveModal = ({
  timesheet,
  types,
  onClose,
}: {
  timesheet: Timesheet;
  types: ReferenceRef[];
  onClose: () => void;
}) => {
  const id = useId();
  const create = useCreateLeave();
  const [typeId, setTypeId] = useState(types[0]?.id ?? '');
  const [dates, setDates] = useState<DateRange>({ from: '', to: '' });
  const [halfStart, setHalfStart] = useState(false);
  const [halfEnd, setHalfEnd] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const single = dates.from === dates.to;
  const submit = () => {
    if (!typeId || !parseDateKey(dates.from) || !parseDateKey(dates.to) || dates.to < dates.from) {
      setError('Choisissez un type et une période complète.');
      return;
    }
    if (
      dates.from < timesheet.mission.startDate ||
      (timesheet.mission.endDate && dates.to > timesheet.mission.endDate)
    ) {
      setError('La période doit être comprise dans les dates de mission.');
      return;
    }
    setError('');
    create.mutate(
      {
        missionId: timesheet.mission.id,
        typeId,
        startDate: dates.from,
        endDate: dates.to,
        halfDayStart: halfStart,
        halfDayEnd: single ? false : halfEnd,
        comment: comment.trim() || null,
      },
      { onSuccess: onClose },
    );
  };
  return (
    <Modal
      title="Ajouter une absence"
      icon={<CalendarIcon />}
      description={timesheet.mission.title || timesheet.mission.projectName}
      onClose={onClose}
      closeDisabled={create.isPending}
      footer={
        <div className="cra-extra-form__footer">
          <Button size="sm" variant="secondary" disabled={create.isPending} onClick={onClose}>
            Annuler
          </Button>
          <Button
            size="sm"
            type="submit"
            form={id}
            loading={create.isPending}
            disabled={create.isPending || !typeId}
            loadingLabel="Envoi…"
          >
            Créer la demande
          </Button>
        </div>
      }
    >
      <form
        id={id}
        onSubmit={(event) => {
          event.preventDefault();
          if (!create.isPending) submit();
        }}
      >
        <fieldset className="cra-extra-form" disabled={create.isPending}>
          <legend className="sr-only">Demande d’absence</legend>
          <Select
            label="Type d’absence"
            value={typeId}
            required
            options={types.map((type) => ({ value: type.id, label: type.label }))}
            onChange={(event) => setTypeId(event.target.value)}
          />
          <DatePicker
            mode="flexible"
            label="Période d’absence"
            value={dates}
            min={timesheet.mission.startDate}
            max={timesheet.mission.endDate ?? undefined}
            onValueChange={(value) => {
              setDates(value);
              if (value.from === value.to) setHalfEnd(false);
            }}
          />
          <div className="cra-extra-form__checks">
            <Checkbox
              label={single ? 'Demi-journée' : 'Demi-journée le premier jour'}
              checked={halfStart}
              onChange={(event) => setHalfStart(event.target.checked)}
            />
            {!single && (
              <Checkbox
                label="Demi-journée le dernier jour"
                checked={halfEnd}
                onChange={(event) => setHalfEnd(event.target.checked)}
              />
            )}
          </div>
          <Textarea
            label="Commentaire"
            placeholder="Précisions utiles pour la validation…"
            rows={3}
            maxLength={2000}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <p className="cra-extra-form__hint">
            La demande sera en attente de validation. Les jours ouvrés sont calculés automatiquement
            ; les absences acceptées alimentent les CRA modifiables.
          </p>
        </fieldset>
        {(error || create.error) && (
          <p role="alert" className="cra-notice">
            {error || create.error?.message}
          </p>
        )}
      </form>
    </Modal>
  );
};
